/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { updateStore } from "../../api/mutations";
import { useLanguage } from "../../contexts/LanguageContext";
import { getApiErrorMessages } from "../../utils/apiErrorReasons";
import { ErrorMessages } from "../../components/ui/Modal";
import { buttonClass, cardClass } from "../../components/ui/styles";
import ImageCropModal, { CROP_TYPES } from "../../components/ImageCropModal";
import { makeImageSquare } from "./logoImage";
import { baseStorePayload, HEX_COLOR } from "./storePayload";

// The API takes PNG or JPEG up to 5 MB (store.rb). The crop always produces a
// PNG, so the size check is on the cropped file — that is what gets uploaded.
const LOGO_TYPES = ["image/png", "image/jpeg"];
const LOGO_MAX_BYTES = 5 * 1024 * 1024;

const COLOR_FIELDS = [
  {
    key: "background_color",
    labelKey: "store.backgroundColor",
    fallback: "#FFFFFF",
    payload: "backgroundColor",
  },
  {
    key: "primary_color",
    labelKey: "store.primaryColor",
    fallback: "#3B82F6",
    payload: "primaryColor",
  },
  {
    key: "secondary_color",
    labelKey: "store.secondaryColor",
    fallback: "#1E40AF",
    payload: "secondaryColor",
  },
];

// Stored colours are not all strict #RRGGBB (prod has "#ffed00 ", "000000",
// "#0000", " #17365c"…). They are trimmed for display, and only a field the
// merchant changes is validated and sent: an untouched odd value stays as it
// is in the database and never blocks saving something else, like a new logo.
function initialColors(store) {
  return Object.fromEntries(
    COLOR_FIELDS.map((f) => [
      f.key,
      (typeof store[f.key] === "string" ? store[f.key].trim() : "") ||
        f.fallback,
    ]),
  );
}

export default function BrandingSection({ store }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const fileInput = useRef(null);

  // The baseline moves to what was sent after each successful save.
  const [savedColors, setSavedColors] = useState(() => initialColors(store));
  const [colors, setColors] = useState(savedColors);
  const [logo, setLogo] = useState(null); // cropped Blob waiting to be saved
  const [logoError, setLogoError] = useState("");
  const [crop, setCrop] = useState(null); // { url, fileName }
  const [saved, setSaved] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(store.logo_url || null);

  // Blob URLs are revoked by hand, exactly once, when replaced or on unmount.
  // Not in a state updater (StrictMode double-invokes those) nor an effect
  // keyed on previewUrl (StrictMode's simulated unmount would kill it).
  const previewBlobRef = useRef(null);
  const setPreview = useCallback((next) => {
    if (previewBlobRef.current && previewBlobRef.current !== next) {
      URL.revokeObjectURL(previewBlobRef.current);
    }
    previewBlobRef.current = next?.startsWith("blob:") ? next : null;
    setPreviewUrl(next);
  }, []);
  useEffect(
    () => () => {
      if (previewBlobRef.current) URL.revokeObjectURL(previewBlobRef.current);
    },
    [],
  );

  const mutation = useMutation({
    mutationFn: updateStore,
    onSuccess: async (_data, variables) => {
      setSavedColors((prev) => ({
        ...prev,
        ...Object.fromEntries(
          COLOR_FIELDS.filter((f) => f.payload in variables).map((f) => [
            f.key,
            variables[f.payload],
          ]),
        ),
      }));
      setLogo(null);
      await queryClient.invalidateQueries({ queryKey: ["stores"] });
      setSaved(true);
    },
  });

  const changedColors = COLOR_FIELDS.filter(
    (f) => colors[f.key] !== savedColors[f.key],
  );
  const colorErrors = Object.fromEntries(
    changedColors
      .filter((f) => !HEX_COLOR.test(colors[f.key]))
      .map((f) => [f.key, t("settings.branding.invalidHex")]),
  );
  const hasColorError = Object.keys(colorErrors).length > 0;

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      setLogoError(t("settings.logo.wrongType"));
      return;
    }
    setLogoError("");
    const unreadable = () => setLogoError(t("settings.logo.readError"));
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setCrop({
          url: await makeImageSquare(reader.result),
          fileName: file.name,
        });
      } catch {
        unreadable();
      }
    };
    reader.onerror = unreadable;
    reader.readAsDataURL(file);
  };

  const closeCrop = () => {
    if (crop?.url) URL.revokeObjectURL(crop.url);
    setCrop(null);
  };

  // ImageCropModal calls onClose (closeCrop) right after this.
  const onCropped = (blob) => {
    if (blob.size > LOGO_MAX_BYTES) {
      setLogoError(t("settings.logo.tooLarge"));
      return;
    }
    setLogoError("");
    setSaved(false);
    setLogo(blob);
    setPreview(URL.createObjectURL(blob));
  };

  const submit = (e) => {
    e.preventDefault();
    if (hasColorError) return;
    setSaved(false);
    mutation.mutate({
      ...baseStorePayload(store),
      ...Object.fromEntries(
        changedColors.map((f) => [f.payload, colors[f.key]]),
      ),
      ...(logo ? { logo } : {}),
    });
  };

  const hasLogo = !!previewUrl;
  const setColor = (key, value) => {
    setSaved(false);
    setColors((c) => ({ ...c, [key]: value }));
  };
  // The native picker only takes a full #rrggbb; while the text is mid-edit
  // (or the stored value is malformed) it shows the last valid colour.
  const fallbackOf = (key) => COLOR_FIELDS.find((f) => f.key === key).fallback;
  const pickerValue = (key) =>
    HEX_COLOR.test(colors[key])
      ? colors[key]
      : HEX_COLOR.test(savedColors[key])
        ? savedColors[key]
        : fallbackOf(key);

  return (
    <form
      onSubmit={submit}
      noValidate
      className={`${cardClass} flex flex-wrap gap-7 p-6`}
    >
      <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-[18px]">
        <h2 className="text-[17px] font-semibold text-gray-900">
          {t("settings.sections.branding")}
        </h2>

        <div className="flex flex-wrap items-center gap-4">
          {hasLogo ? (
            <img
              src={previewUrl}
              alt={t("store.logoPreview")}
              className="h-[72px] w-[72px] rounded-[18px] border border-gray-200 object-contain"
            />
          ) : (
            <span
              aria-hidden="true"
              className="flex h-[72px] w-[72px] items-center justify-center rounded-[18px] border-[1.5px] border-dashed border-gray-300 bg-gray-50 text-gray-500"
            >
              <PhotoIcon className="h-6 w-6" />
            </span>
          )}
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className={buttonClass.secondary}
              >
                {hasLogo
                  ? t("settings.logo.replace")
                  : t("settings.logo.upload")}
              </button>
            </div>
            <span className="text-xs text-gray-500">
              {t("settings.logo.hint")}
            </span>
            <input
              ref={fileInput}
              type="file"
              name="logo-upload"
              accept={LOGO_TYPES.join(",")}
              onChange={pickFile}
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
            />
          </div>
        </div>

        {logoError && (
          <p role="alert" className="text-[13px] text-red-700">
            {logoError}
          </p>
        )}

        <fieldset className="flex flex-col gap-2.5">
          <legend className="text-[13px] font-semibold text-gray-700">
            {t("store.brandColors")}
          </legend>
          <p className="text-xs leading-normal text-gray-600">
            {t("settings.branding.colorsHint")}
          </p>
          {COLOR_FIELDS.map((field) => {
            const error = colorErrors[field.key];
            const errorId = `${field.key}-error`;
            return (
              <div key={field.key} className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={pickerValue(field.key)}
                    onChange={(e) => setColor(field.key, e.target.value)}
                    aria-label={t(field.labelKey)}
                    className="h-11 w-11 cursor-pointer rounded-[10px] border border-gray-300 bg-white p-0.5"
                  />
                  <label className="flex flex-col gap-1 text-sm font-medium text-gray-900">
                    {t(field.labelKey)}
                    <input
                      type="text"
                      name={field.key}
                      value={colors[field.key]}
                      onChange={(e) =>
                        setColor(field.key, e.target.value.trim())
                      }
                      spellCheck={false}
                      autoComplete="off"
                      maxLength={7}
                      aria-invalid={error ? true : undefined}
                      aria-describedby={error ? errorId : undefined}
                      className={`min-h-9 w-32 rounded-lg border px-2.5 font-mono text-[13px] font-normal text-gray-900 uppercase ${error ? "border-red-400" : "border-gray-300"}`}
                    />
                  </label>
                </div>
                {error && (
                  <span id={errorId} className="text-[13px] text-red-700">
                    {error}
                  </span>
                )}
              </div>
            );
          })}
          <Link
            to="/passes"
            className="self-start text-[13px] font-semibold text-indigo-700 hover:text-indigo-800"
          >
            {t("settings.branding.cardColorsLink")}
          </Link>
        </fieldset>
      </div>

      <div className="flex flex-[1_1_300px] flex-col items-start gap-2.5">
        <span className="text-[13px] font-semibold text-gray-700">
          {t("settings.branding.previewTitle")}
        </span>
        <CardPreview
          name={store.name}
          logoUrl={previewUrl}
          background={pickerValue("background_color")}
          text={pickerValue("primary_color")}
          label={t("settings.branding.previewLabel")}
        />
        {mutation.isError && (
          <ErrorMessages
            className="w-full"
            messages={getApiErrorMessages(
              mutation.error,
              t,
              "errors.updateStoreFailed",
            )}
          />
        )}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={mutation.isPending || hasColorError}
            className={buttonClass.primary}
          >
            {mutation.isPending
              ? t("common.saving")
              : t("settings.branding.save")}
          </button>
          <span role="status" className="text-sm text-emerald-800">
            {saved ? t("common.savedChanges") : ""}
          </span>
        </div>
      </div>

      <ImageCropModal
        variant="redesign"
        type={CROP_TYPES.LOGO}
        isOpen={!!crop?.url}
        imageUrl={crop?.url}
        onClose={closeCrop}
        onCropComplete={onCropped}
        output="blob"
        outputSize="crop"
        outputFileName="cropped-logo.png"
        title={t("settings.logo.crop")}
        description={
          crop?.fileName
            ? `${crop.fileName}. ${t("settings.logo.cropHint")}`
            : t("settings.logo.cropHint")
        }
        saveLabel={t("settings.logo.useImage")}
        cancelLabel={t("common.cancel")}
      />
    </form>
  );
}

function CardPreview({ name, logoUrl, background, text, label }) {
  return (
    <div
      role="img"
      aria-label={label}
      className="flex w-full max-w-[340px] flex-col gap-[18px] rounded-2xl p-[18px] shadow-lg"
      style={{ background }}
    >
      <div className="flex items-center gap-2.5">
        {logoUrl && (
          <img
            src={logoUrl}
            alt=""
            className="h-8 w-8 rounded-lg bg-white/10 object-contain"
          />
        )}
        <span
          className="truncate text-sm font-semibold tracking-wide"
          style={{ color: text }}
        >
          {name}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5" aria-hidden="true">
        {Array.from({ length: 10 }, (_, i) => (
          <span
            key={i}
            className="h-5 w-5 rounded-full border-2"
            style={{
              borderColor: text,
              background: i < 6 ? text : "transparent",
            }}
          />
        ))}
      </div>
    </div>
  );
}
